import {
  Box,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import AppDialog from "components/common/AppDialog";
import CustomButton from "components/common/CustomButton";
import {
  ClientRunHistoryItem,
  useGetClientRunHistoryItemQuery,
} from "features/integrationsBeta/integrationsBeta";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useEffect } from "react";
import { formatIsoDateTime, formatIsoDuration } from "utils/dateUtils";
import ClientRunStatusChip from "./ClientRunStatusChip";

type Props = {
  open: boolean;
  runId: number | null;
  /** The list row, shown until the item itself has loaded. */
  placeholder?: ClientRunHistoryItem | null;
  onClose: () => void;
  displayZone?: string;
  clientStatusLabel: (value: string | null | undefined) => string;
  /** Internal users only: cross-link to the run's internal drill-down. */
  onOpenInternalRun?: () => void;
};

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ width: 96, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" component="div" sx={{ minWidth: 0 }}>
        {children}
      </Typography>
    </Box>
  );
}

/**
 * One run as the client sees it (CAL-5776): status, timing and the note
 * Calibrate shared - no re-runs and no sign of a status set by hand
 * (CAL-6165). Fetched by id so `?run=` deep links work regardless of the list
 * page; a run the client does not see is not found.
 */
export default function ClientRunDetailsDialog({
  open,
  runId,
  placeholder,
  onClose,
  displayZone,
  clientStatusLabel,
  onOpenInternalRun,
}: Props) {
  const dispatch = useAppDispatch();
  const { currentData, isError } = useGetClientRunHistoryItemQuery(runId ?? 0, {
    skip: !open || runId === null,
  });
  const item =
    currentData ??
    (placeholder && placeholder.id === runId ? placeholder : null);

  useEffect(() => {
    if (!open || !isError) return;
    dispatch(
      pushNotification({ severity: "error", message: "Run not found." }),
    );
    onClose();
  }, [open, isError, dispatch, onClose]);

  const formatDate = (iso: string) => formatIsoDateTime(iso, displayZone);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="Run details"
      maxWidth="sm"
      showCloseIcon
      actions={
        <>
          {onOpenInternalRun && (
            <CustomButton fullWidth={false} onClick={onOpenInternalRun}>
              Open internal run
            </CustomButton>
          )}
          <CustomButton fullWidth={false} onClick={onClose}>
            Close
          </CustomButton>
        </>
      }
    >
      {!item ? (
        <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <ClientRunStatusChip
              status={item.client_status}
              label={clientStatusLabel(item.client_status)}
            />
          </Box>

          <Stack spacing={0.75}>
            <Row label="Started">{formatDate(item.started_at)}</Row>
            <Row label="Finished">
              {item.finished_at ? formatDate(item.finished_at) : "—"}
            </Row>
            <Row label="Duration">
              {formatIsoDuration(item.started_at, item.finished_at)}
            </Row>
          </Stack>

          <Divider />

          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Note from Calibrate
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {item.note || "No notes for this run."}
            </Typography>
          </Box>
        </Stack>
      )}
    </AppDialog>
  );
}
